import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Landmark, Globe2, Cpu, Users } from "lucide-react";
import {
  NA_PRIMARY,
  NA_SECONDARY,
  NA_ACCENT,
  naGradientLine,
  naGradientTitleStyle,
  naDarkCardBase,
  naReadableText,
  naMutedText,
  naCardBgHover,
  onDarkCardEnter,
  onDarkCardLeave,
} from "./naSectionTokens";
import { EVENT_LOCATION } from "../../constants";

const highlightCards = [
  {
    icon: Landmark,
    title: "Intelligent infrastructure",
    text: "Cities, transportation, construction, airports, utilities, and urban planning on one stage.",
    color: NA_PRIMARY,
  },
  {
    icon: Globe2,
    title: "North American hub",
    text: "The Curtis Hotel, Denver — a connected hub for infrastructure, construction, and digital innovation leaders across North America.",
    color: NA_SECONDARY,
  },
  {
    icon: Cpu,
    title: "Digital twin depth",
    text: "Smarter decisions, predictive operations, resilient assets, and AI-driven urban management.",
    color: NA_PRIMARY,
  },
  {
    icon: Users,
    title: "Who meets here",
    text: "Public sector, owners, planners, agencies, innovators, engineers, and technology providers.",
    color: NA_SECONDARY,
  },
];

function NAEventOverview() {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

  return (
    <div className="section py-5" style={{ background: "transparent", position: "relative", zIndex: 1 }}>
      <Container>
        <Row className="justify-content-center mb-4">
          <Col md={11} lg={10} className="text-center">
            <p
              className="mb-3"
              style={{
                fontSize: size ? "1.25rem" : "1.05rem",
                fontWeight: 600,
                color: NA_ACCENT,
                ...naReadableText,
              }}
            >
              {EVENT_LOCATION}
            </p>
            <p
              className="mb-0"
              style={{
                fontSize: size ? "1.2rem" : "1.05rem",
                lineHeight: 1.65,
                fontWeight: 600,
                ...naMutedText,
              }}
            >
              Shaping the future of intelligent infrastructure, smart cities &amp; connected urban
              systems.
            </p>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col className="ml-auto mr-auto text-center" md={12}>
            <h2
              className="text-center mb-3"
              style={{
                fontSize: size ? "2.75rem" : "1.85rem",
                fontWeight: 700,
                letterSpacing: "0.5px",
                ...naGradientTitleStyle,
              }}
            >
              Summary
            </h2>
            <div
              style={{
                width: "120px",
                height: "4px",
                borderRadius: "2px",
                margin: "0 auto 2rem",
                ...naGradientLine,
              }}
            />
          </Col>
        </Row>

        <Row className="justify-content-center mb-4">
          <Col md={6} className="mb-4">
            <Card
              style={{
                ...naDarkCardBase,
                borderRadius: "12px",
                padding: size ? "2rem" : "1.5rem",
                height: "100%",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => onDarkCardEnter(e, NA_PRIMARY)}
              onMouseLeave={onDarkCardLeave}
            >
              <p
                className="mb-4"
                style={{
                  fontSize: size ? "1.08rem" : "0.98rem",
                  lineHeight: 1.8,
                  ...naReadableText,
                }}
              >
                The North America: DIGITAL TWIN 2026 will unite geospatial experts, public sector leaders,
                infrastructure owners, city planners, construction
                innovators, digital engineering experts, and technology providers to explore how
                digital twins are transforming the future of cities and infrastructure across
                North America.
              </p>
              <p
                className="mb-0"
                style={{
                  fontSize: size ? "1.08rem" : "0.98rem",
                  lineHeight: 1.8,
                  ...naReadableText,
                }}
              >
                From smart cities and transportation systems to construction, airports, utilities,
                and urban planning, the event will showcase how digital twin technologies are
                enabling smarter decision-making, predictive operations, resilient infrastructure,
                and AI-driven urban management.
              </p>
            </Card>
          </Col>
          <Col md={6} className="mb-4">
            <Row>
              {highlightCards.map((item, i) => {
                const Icon = item.icon;
                return (
                  <Col xs={12} sm={6} key={i} className="mb-3">
                    <Card
                      style={{
                        ...naDarkCardBase,
                        borderRadius: "16px",
                        padding: size ? "1.35rem" : "1.1rem",
                        height: "100%",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => onDarkCardEnter(e, item.color)}
                      onMouseLeave={onDarkCardLeave}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "14px",
                          background: `linear-gradient(135deg, ${item.color}33, ${item.color}14)`,
                          border: `2px solid ${item.color}55`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "0.85rem",
                        }}
                      >
                        <Icon size={24} strokeWidth={2.5} style={{ color: item.color }} />
                      </div>
                      <h3
                        className="mb-2"
                        style={{ fontSize: "0.92rem", fontWeight: 700, ...naReadableText }}
                      >
                        {item.title}
                      </h3>
                      <p className="mb-0" style={{ fontSize: "0.84rem", lineHeight: 1.55, ...naMutedText }}>
                        {item.text}
                      </p>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Col>
          <Card
            style={{
              ...naDarkCardBase,
              border: `1px solid ${NA_PRIMARY}44`,
              borderRadius: "12px",
              padding: size ? "1.5rem" : "1.25rem",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = naCardBgHover;
            }}
            className="mx-3"
            onMouseLeave={onDarkCardLeave}
          >
            <p
              className="mb-0"
              style={{
                fontSize: size ? "1.05rem" : "0.98rem",
                lineHeight: 1.75,
                fontWeight: 500,
                ...naReadableText,
              }}
            >
              Hosted at The Curtis Hotel, Denver is one of North America’s fastest-growing centers for infrastructure development, geospatial innovation, and digital construction — providing the ideal setting for a high-level gathering focused on the future of intelligent infrastructure. Its central location and strong ecosystem of engineering, construction, transportation, and technology organizations make it a natural destination for advancing the digital twin conversation across the continent.
            </p>
          </Card>
        </Row>
      </Container>
    </div>
  );
}

export default NAEventOverview;
